import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Zap, RefreshCw, Loader2, Wallet as WalletIcon, TrendingDown,
  Gift, ShoppingCart, ShieldCheck, Undo2, Lock, ArrowLeft, Receipt,
} from 'lucide-react';
import { Button } from '../ui/button';
import { useAuth } from '../context/AuthContext';
import { BuyCreditsModal } from '../wallet/BuyCreditsModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


const TX_META = {
  ONBOARDING_BONUS: { label: 'Welcome bonus',    icon: Gift,        color: 'text-emerald-700 bg-emerald-50 border-emerald-200 dark:text-emerald-300 dark:bg-emerald-500/10 dark:border-emerald-500/30' },
  TOPUP_PURCHASE:   { label: 'Top-up',           icon: ShoppingCart,color: 'text-sky-700 bg-sky-50 border-sky-200 dark:text-sky-300 dark:bg-sky-500/10 dark:border-sky-500/30' },
  USAGE_HOLD:       { label: 'Reserved',         icon: TrendingDown,color: 'text-amber-700 bg-amber-50 border-amber-200 dark:text-amber-300 dark:bg-amber-500/10 dark:border-amber-500/30' },
  USAGE_SUCCESS:    { label: 'Cloud sync',       icon: ShieldCheck, color: 'text-rose-700 bg-rose-50 border-rose-200 dark:text-rose-300 dark:bg-rose-500/10 dark:border-rose-500/30' },
  USAGE_REFUND:     { label: 'Refund',           icon: Undo2,       color: 'text-emerald-700 bg-emerald-50 border-emerald-200 dark:text-emerald-300 dark:bg-emerald-500/10 dark:border-emerald-500/30' },
  ADMIN_ADJUSTMENT: { label: 'Admin adjustment', icon: ShieldCheck, color: 'text-purple-700 bg-purple-50 border-purple-200 dark:text-purple-300 dark:bg-purple-500/10 dark:border-purple-500/30' },
};


function fmt(dt) {
  if (!dt) return '—';
  try {
    return new Date(dt).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true,
    });
  } catch { return dt; }
}


export default function WalletPage() {
  const { user, loading: authLoading } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [packs, setPacks] = useState({});
  const [txs, setTxs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  const isSandbox = (user?.organization_id || '').startsWith('TEMP_ORG_');

  const refresh = useCallback(async () => {
    if (!user || isSandbox) return;
    setLoading(true);
    try {
      const [w, t] = await Promise.all([
        axios.get(`${API}/wallet`),
        axios.get(`${API}/wallet/transactions?page=${page}&page_size=${pageSize}`),
      ]);
      setWallet(w.data.wallet);
      setPacks(w.data.packs || {});
      setTxs(t.data.items || []);
      setTotal(t.data.total || 0);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load wallet');
    } finally { setLoading(false); }
  }, [user, isSandbox, page, pageSize]);

  useEffect(() => { refresh(); }, [refresh]);

  if (authLoading) return null;
  if (!user) return <Navigate to="/auth" replace />;

  if (isSandbox) {
    return (
      <div className="p-8 text-center" data-testid="wallet-sandbox-block">
        <div className="max-w-md mx-auto rounded-xl border border-amber-200 bg-amber-50 dark:border-amber-500/30 dark:bg-amber-500/5 p-6">
          <WalletIcon className="w-10 h-10 text-amber-500 mx-auto mb-3" />
          <div className="text-lg font-semibold text-slate-900 dark:text-white mb-1">Wallet unavailable in demo</div>
          <div className="text-sm text-slate-600 dark:text-slate-400">
            Credit-wallet is a live-account feature. Redeem a real Pro licence to unlock it — you'll be gifted 200 free credits on activation.
          </div>
        </div>
      </div>
    );
  }

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="min-h-screen p-4 md:p-8" data-testid="wallet-page">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <Link to="/dashboard" data-testid="wallet-back" className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800">
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <div className="flex-1">
            <h1 className="text-2xl font-bold flex items-center gap-2 text-theme-primary-strong">
              <WalletIcon className="w-6 h-6 text-theme-primary" /> Credit Wallet
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              1 credit = ₹1 (GST-inclusive) · Cloud-sync flows spend credits · Offline JSON/Excel exports are always free
            </p>
          </div>
          <Button
            onClick={refresh} disabled={loading} variant="outline" size="sm"
            data-testid="wallet-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>

        {/* Balance hero */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div
            data-testid="wallet-balance-hero"
            className="md:col-span-2 rounded-2xl p-6 bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden relative"
          >
            <div
              className="absolute -top-8 -right-8 w-32 h-32 rounded-full blur-2xl opacity-40"
              style={{ backgroundColor: 'var(--theme-primary-light)' }}
            />
            <div className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-2">Available balance</div>
            <div className="flex items-baseline gap-2">
              <span
                className="text-5xl font-bold tabular-nums text-theme-primary-strong"
                data-testid="wallet-balance"
              >
                {loading ? '…' : (wallet?.balance ?? 0).toLocaleString('en-IN')}
              </span>
              <span className="text-slate-500 dark:text-slate-400">credits</span>
              {wallet?.is_locked && (
                <span className="ml-3 text-[10px] uppercase tracking-wider font-bold bg-rose-50 text-rose-700 border border-rose-200 dark:bg-rose-500/20 dark:text-rose-300 dark:border-rose-500/40 px-2 py-0.5 rounded flex items-center gap-1">
                  <Lock className="w-3 h-3" /> Locked
                </span>
              )}
            </div>
            <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400">Lifetime top-ups</div>
                <div className="font-mono tabular-nums text-emerald-600 dark:text-emerald-400">
                  +{(wallet?.lifetime_topup ?? 0).toLocaleString('en-IN')}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400">Lifetime usage</div>
                <div className="font-mono tabular-nums text-rose-600 dark:text-rose-400">
                  −{(wallet?.lifetime_usage ?? 0).toLocaleString('en-IN')}
                </div>
              </div>
            </div>
            <Button
              onClick={() => setModalOpen(true)}
              disabled={loading || wallet?.is_locked}
              data-testid="wallet-buy-btn"
              size="lg"
              className="mt-6 bg-theme-primary hover:bg-theme-primary font-semibold"
            >
              <Zap className="w-4 h-4 mr-1.5" /> Buy more credits
            </Button>
          </div>

          <div className="rounded-2xl p-5 bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 shadow-sm text-sm space-y-3" data-testid="wallet-pricing-card">
            <div className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400">What credits burn</div>
            <ul className="space-y-1.5 text-slate-700 dark:text-slate-300">
              <li className="flex justify-between"><span>1× E-Invoice (IRN + QR)</span><span className="font-mono font-semibold">1 credit</span></li>
              <li className="flex justify-between"><span>1× E-Way Bill (standalone)</span><span className="font-mono font-semibold">1 credit</span></li>
              <li className="flex justify-between"><span>IRN + EWB (combined)</span><span className="font-mono font-semibold">2 credits</span></li>
              <li className="flex justify-between text-emerald-700 dark:text-emerald-400"><span>JSON / Excel offline export</span><span className="font-mono font-semibold">0 credits</span></li>
              <li className="flex justify-between text-emerald-700 dark:text-emerald-400"><span>Schema-rejected API call</span><span className="font-mono font-semibold">Auto-refund</span></li>
            </ul>
          </div>
        </div>

        {/* Transaction history */}
        <div className="rounded-2xl bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
            <div className="font-semibold text-slate-700 dark:text-slate-200">Transaction history</div>
            <div className="text-xs text-slate-500">{total.toLocaleString('en-IN')} entries</div>
          </div>
          {loading ? (
            <div className="p-8 flex items-center justify-center gap-2 text-slate-400 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : txs.length === 0 ? (
            <div className="p-10 text-center text-slate-500 text-sm">No wallet activity yet.</div>
          ) : (
            <table className="w-full text-sm" data-testid="wallet-tx-table">
              <thead className="bg-slate-50 dark:bg-slate-950/40">
                <tr className="text-[10px] uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-2 text-left w-44">Type</th>
                  <th className="px-4 py-2 text-left w-40">When</th>
                  <th className="px-4 py-2 text-left">Note</th>
                  <th className="px-4 py-2 text-right w-24">Amount</th>
                  <th className="px-4 py-2 text-right w-24">Balance</th>
                  <th className="px-4 py-2 text-left w-20">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {txs.map((tx) => {
                  const meta = TX_META[tx.type] || TX_META.ADMIN_ADJUSTMENT;
                  const Icon = meta.icon;
                  const pos = tx.amount >= 0;
                  return (
                    <tr key={tx.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40" data-testid={`wallet-tx-${tx.id}`}>
                      <td className="px-4 py-2.5">
                        <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded border ${meta.color}`}>
                          <Icon className="w-3 h-3" /> {meta.label}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-xs text-slate-500 whitespace-nowrap">{fmt(tx.created_at)}</td>
                      <td className="px-4 py-2.5 text-slate-700 dark:text-slate-300 truncate max-w-md">
                        <div className="truncate">{tx.notes}</div>
                        {tx.reference_id && (
                          <div className="text-[10px] text-slate-500 font-mono truncate">ref: {tx.reference_id}</div>
                        )}
                      </td>
                      <td className={`px-4 py-2.5 text-right font-mono tabular-nums text-sm ${pos ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                        {pos ? '+' : ''}{tx.amount}
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono tabular-nums text-slate-500 dark:text-slate-400 text-xs">
                        {tx.balance_after ?? '—'}
                      </td>
                      <td className="px-4 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">
                        <div className="flex items-center gap-2">
                          <span>{tx.status}</span>
                          {tx.type === 'TOPUP_PURCHASE' && tx.status === 'completed' && (
                            <a
                              href={`${API}/wallet/transactions/${tx.id}/receipt`}
                              target="_blank" rel="noreferrer"
                              data-testid={`wallet-receipt-${tx.id}`}
                              className="inline-flex items-center gap-1 text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 normal-case tracking-normal font-medium"
                              title="Download the GST tax invoice for this top-up"
                              onClick={(e) => {
                                // Attach the user's bearer so the PDF stream is authed.
                                e.preventDefault();
                                const tok = localStorage.getItem('token');
                                fetch(e.currentTarget.href, { headers: { Authorization: `Bearer ${tok}` } })
                                  .then((r) => r.ok ? r.blob() : Promise.reject(r.statusText))
                                  .then((blob) => {
                                    const url = URL.createObjectURL(blob);
                                    const a = document.createElement('a');
                                    a.href = url;
                                    a.download = `RupiWise-Receipt-${tx.id.slice(0, 8)}.pdf`;
                                    document.body.appendChild(a); a.click(); a.remove();
                                    setTimeout(() => URL.revokeObjectURL(url), 1000);
                                  })
                                  .catch(() => toast.error('Receipt not ready yet — try again in a moment'));
                              }}
                            >
                              <Receipt className="w-3 h-3" /> Receipt
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {total > pageSize && (
            <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between text-xs">
              <div>Page {page} of {totalPages}</div>
              <div className="flex gap-2">
                <Button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || loading} variant="outline" size="sm">Prev</Button>
                <Button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages || loading} variant="outline" size="sm">Next</Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <BuyCreditsModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        packs={packs}
        user={user}
        onCreditsAdded={(w) => { setWallet(w); refresh(); }}
      />
    </div>
  );
}
